<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { IonPage } from '@ionic/vue';
import { useDataStore } from '@/store/data';
import KiwishHeader from '@/views/components/KiwishHeader.vue';

const router = useRouter();
const route = useRoute();
const dataStore = useDataStore();

const matchData = ref(null);

const goBack = () => {
  router.push('/app/matchs');
};

// Computed para obtener los valores
const matchName = computed(() => matchData.value?.name || '');
const matchPhone = computed(() => {
  const phone = matchData.value?.phone || '';
  // Separar de 3 en 3
  return phone.replace(/(\d{3})(?=\d)/g, '$1 ');
});
const fullPhone = computed(() => {
  const prefix = matchData.value?.prefix || '';
  const phone = matchData.value?.phone || '';
  return `${prefix}${phone}`;
});
const matchHint = computed(() => matchData.value?.hint || '');
const matchMessage = computed(() => matchData.value?.message || '');

// Acciones de contacto
const callPhone = () => {
  if (fullPhone.value) {
    window.location.href = `tel:${fullPhone.value}`;
  }
};

const openWhatsApp = () => {
  if (fullPhone.value) {
    window.open(`https://wa.me/${fullPhone.value.replace('+', '')}`, '_blank');
  }
};

const sendSMS = () => {
  if (fullPhone.value) {
    window.location.href = `sms:${fullPhone.value}`;
  }
};

const loadMatchData = async () => {
  const indexParam = route.query.index;
  const matchIndex = indexParam !== undefined ? parseInt(String(indexParam)) : -1;

  // Si no hay matchs en el store, intentar cargarlos
  if (!dataStore.matchs || dataStore.matchs.length === 0) {
    await dataStore.loadData();
  }

  if (!isNaN(matchIndex) && matchIndex >= 0 && dataStore.matchs && dataStore.matchs[matchIndex]) {
    matchData.value = { ...dataStore.matchs[matchIndex] };
  }
};

onMounted(async () => {
  await loadMatchData();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'view_match',
  });
});

// Recargar datos cuando cambie el index en la query
watch(() => route.query.index, async () => {
  await loadMatchData();
});
</script>

<template>
  <ion-page class="match-detail-page bg-gradient-secrets">
    <KiwishHeader variant="dark" :hideMenu="true" />

    <div class="match-detail-container">
      <!-- Contenido -->
      <div class="match-content">
        <!-- Título / Nombre -->
        <h1 class="match-name">{{ matchName }}</h1>

        <!-- Teléfono con acciones -->
        <div class="phone-container">
          <div class="phone-info">
            <svg class="phone-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
            <span class="phone-number">{{ matchPhone }}</span>
          </div>
          <div class="contact-actions">
            <button class="action-button" @click="callPhone" title="Llamar">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
            </button>
            <button class="action-button whatsapp" @click="openWhatsApp" title="WhatsApp">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
            <button class="action-button" @click="sendSMS" title="SMS">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Pista -->
        <div class="match-field">
          <span class="field-label">{{ $t('matchs.modal.hint') }}:</span>
          <span class="field-text">&nbsp;{{ matchHint || $t('matchs.no.hint') }}</span>
        </div>

        <!-- Separador -->
        <div class="separator"></div>

        <!-- Mensaje -->
        <div class="match-field">
          <span class="field-label">{{ $t('matchs.modal.message') }}:</span>
          <span class="field-text">&nbsp;{{ matchMessage || $t('matchs.no.message') }}</span>
        </div>
      </div>

      <!-- Botón cerrar -->
      <button class="btn-close" @click="goBack">
        {{ $t('close') }}
      </button>
    </div>
  </ion-page>
</template>

<style scoped>
.match-detail-page {
  --background: transparent;
}

.match-detail-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 30px;
  padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 30px);
  position: relative;
}

.match-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 60px;
}

.match-name {
  font-size: 1.8rem;
  font-weight: 600;
  color: #5A695B;
  margin: 0 0 10px 0;
  line-height: 1.3;
  text-align: left;
}

.phone-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(90, 105, 91, 0.08);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 25px;
}

.phone-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.phone-icon {
  width: 20px;
  height: 20px;
  color: #5A695B;
  opacity: 0.6;
}

.phone-number {
  font-size: 1.05rem;
  font-weight: 500;
  color: #5A695B;
  letter-spacing: 0.5px;
}

.contact-actions {
  display: flex;
  gap: 10px;
}

.action-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #5A695B;
  border: none;
  color: #F8E9BF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  transition: all 0.2s;
}

.action-button svg {
  width: 100%;
  height: 100%;
}

.action-button:active {
  opacity: 0.7;
  transform: scale(0.95);
}

.action-button.whatsapp {
  background: #25D366;
  color: white;
}

.match-field {
  margin-bottom: 20px;
  text-align: left;
}

.field-label {
  font-size: 1.8rem;
  font-weight: 600;
  color: #5A695B;
}

.field-text {
  font-size: 1.1rem;
  font-weight: 400;
  color: #5A695B;
  line-height: 1.5;
}

.separator {
  height: 1px;
  background: #DAB347;
  margin: 10px 0 20px 0;
}

.btn-close {
  background: #EDEAD5;
  border: 1px solid #5A695B;
  border-radius: 16px;
  color: #5A695B;
  font-size: 1rem;
  font-weight: 600;
  padding: 15px 50px;
  cursor: pointer;
  transition: opacity 0.2s;
  margin-top: auto;
  width: 100%;
  max-width: 300px;
  align-self: center;
}

.btn-close:active {
  opacity: 0.7;
}

/* Responsive */
@media (max-height: 600px) {
  .match-name {
    font-size: 1.5rem;
    margin-bottom: 20px;
  }
  .match-content {
    padding-top: 40px;
  }
}
</style>
